import CharacterCount from '@boViews/shared/CharacterCount';
import CollapsibleSection from '@boViews/shared/CollapsibleSection';
import LanguageTabs from '@boViews/shared/LanguageTabs';
import Button from '@sharedComponents/Button';
import FlexBox from '@sharedComponents/FlexBox';
import FormGroup from '@sharedComponents/FormGroup';
import Grid from '@sharedComponents/Grid';
import InputMultiSelectList from '@sharedComponents/InputMultiSelectList';
import InputText from '@sharedComponents/InputText';
import InputTextArea from '@sharedComponents/InputTextArea';
import { REFERENCE_LANGUAGE } from '@src/i18n';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DESCRIPTION_MAX_LENGTH, emptyLanguages, NAME_MAX_LENGTH, toTagOptions } from '../collectionForm';

import * as style from './style.module.css';

/**
 * "Information" tab of the collection page: name and description in the active language, then the three
 * tag selectors. It reads and writes the page form (react-hook-form context); `onSave` submits it.
 */
const InformationsTab = ({
    languages = [],
    tagsOptions = {},
    activeLanguage = REFERENCE_LANGUAGE,
    saving = false,
    onLanguageChange = () => {},
    onSave = () => {},
}) => {
    const { t } = useTranslation('bo');
    const { register, control, clearErrors } = useFormContext();
    const { errors, isDirty } = useFormState({ control });
    const [names, descriptions, typologies = []] = useWatch({ control, name: ['name', 'description', 'tagsTypology'] });
    const name = names?.[activeLanguage] ?? '';
    const description = descriptions?.[activeLanguage] ?? '';
    const nameError = errors.name?.[activeLanguage]?.message;
    // A collection has a single typology; older collections may hold several, to be removed before saving.
    const typologyError = typologies.length > 1 ? t('A collection has a single typology: remove the others.') : undefined;

    return (
        <CollapsibleSection title={t('Information')}>
            <FlexBox flexDirection="column" className={style.languageBox}>
                <LanguageTabs
                    languages={languages}
                    active={activeLanguage}
                    emptyLanguages={emptyLanguages(names, languages)}
                    referenceLanguage={REFERENCE_LANGUAGE}
                    onChange={onLanguageChange}
                />
                <FlexBox flexDirection="column" gap="xs" className={style.fields}>
                    <FormGroup label={t('Name')} errorMsg={nameError}>
                        <InputText
                            key={`name-${activeLanguage}`}
                            aria-label={t('Name')}
                            isError={Boolean(nameError)}
                            {...register(`name.${activeLanguage}`, { onChange: () => clearErrors(`name.${activeLanguage}`) })}
                        />
                        <CharacterCount length={name.length} max={NAME_MAX_LENGTH} />
                    </FormGroup>
                    <FormGroup label={t('Description')}>
                        <InputTextArea
                            key={`description-${activeLanguage}`}
                            aria-label={t('Description')}
                            {...register(`description.${activeLanguage}`)}
                        />
                        <CharacterCount length={description.length} max={DESCRIPTION_MAX_LENGTH} />
                    </FormGroup>
                </FlexBox>
            </FlexBox>
            <Grid>
                <FormGroup label={t('Typology')} errorMsg={typologyError}>
                    <InputMultiSelectList
                        control={control}
                        name="tagsTypology"
                        options={toTagOptions(tagsOptions.typology)}
                        placeholder={t('Select...')}
                        isSearchable={typologies.length === 0}
                        isError={Boolean(typologyError)}
                    />
                </FormGroup>
                <FormGroup label={t('Subdomain')}>
                    <InputMultiSelectList
                        control={control}
                        name="tagsDomain"
                        options={toTagOptions(tagsOptions.domain)}
                        placeholder={t('Select...')}
                    />
                </FormGroup>
                <FormGroup label={t('Family')}>
                    <InputMultiSelectList
                        control={control}
                        name="tagsFamily"
                        options={toTagOptions(tagsOptions.family)}
                        placeholder={t('Select...')}
                    />
                </FormGroup>
            </Grid>
            <FlexBox justifyContent="flex-end">
                <Button
                    label={t('Save modifications')}
                    action={onSave}
                    disabled={!isDirty || saving || Boolean(typologyError)}
                    isLoading={saving}
                />
            </FlexBox>
        </CollapsibleSection>
    );
};

export default InformationsTab;
